/**
 * <s-info-tabs> — accessible tabs (WAI-ARIA tabs pattern) for the product information area.
 * No module imports so it runs on its own.
 *
 * Required markup: [role="tab"][aria-controls] buttons inside [role="tablist"], and matching
 * [role="tabpanel"] elements. Inactive panels carry `hidden`; inactive tabs carry tabindex="-1".
 */
class SInfoTabs extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.addEventListener(
      "click",
      (event) => {
        const tab = event.target.closest('[role="tab"]');
        if (tab && this.contains(tab)) this.#select(tab);
      },
      opts,
    );
    this.addEventListener("keydown", (event) => this.#onKeydown(event), opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #tabs() {
    return Array.from(this.querySelectorAll('[role="tab"]'));
  }

  #select(tab, focus = false) {
    for (const other of this.#tabs()) {
      const active = other === tab;
      other.setAttribute("aria-selected", active ? "true" : "false");
      if (active) other.removeAttribute("tabindex");
      else other.setAttribute("tabindex", "-1");
      const panel = document.getElementById(other.getAttribute("aria-controls"));
      if (panel) panel.hidden = !active;
    }
    if (focus) tab.focus();
  }

  #onKeydown(event) {
    const current = event.target.closest('[role="tab"]');
    if (!current) return;
    const tabs = this.#tabs();
    const index = tabs.indexOf(current);
    const rtl = getComputedStyle(this).direction === "rtl";
    let next = null;
    if (event.key === (rtl ? "ArrowLeft" : "ArrowRight")) next = tabs[(index + 1) % tabs.length];
    else if (event.key === (rtl ? "ArrowRight" : "ArrowLeft")) next = tabs[(index - 1 + tabs.length) % tabs.length];
    else if (event.key === "Home") next = tabs[0];
    else if (event.key === "End") next = tabs[tabs.length - 1];
    if (!next) return;
    event.preventDefault();
    this.#select(next, true);
  }
}

if (!customElements.get("s-info-tabs")) {
  customElements.define("s-info-tabs", SInfoTabs);
}
